import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { formatListDate } from "@/utils/date-format";
import { formatINR } from "@/utils/app-formatters";
import type { AccountsDashboardStats } from "../types";

const TYPE_LABELS: Record<string, string> = {
  sabil: "Sabil",
  lagat: "Lagat",
  booking: "Booking",
  niyaaz: "Niyaaz Event",
  yearlyNiyaaz: "Yearly Niyaaz",
};

type RecentReceiptRow = NonNullable<AccountsDashboardStats["recentReceipts"]>[number];

type RecentReceiptsTableProps = {
  receipts: AccountsDashboardStats["recentReceipts"];
};

function RecentReceiptMobileCard({
  row,
  onOpen,
}: {
  row: RecentReceiptRow;
  onOpen: (listPath: string, id: string) => void;
}) {
  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardActionArea onClick={() => onOpen(row.listPath, row.id)}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {row.receiptNo || "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {row.label || "—"}
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              {formatINR(row.amount)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
            {TYPE_LABELS[row.type] ?? row.type}
            {" · "}
            {row.date ? formatListDate(dayjs(row.date)) : "—"}
            {" · "}
            {row.mode || "—"}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function RecentReceiptsTable({ receipts }: RecentReceiptsTableProps) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const rows = receipts ?? [];

  if (rows.length === 0) {
    return null;
  }

  const handleRowClick = (listPath: string, id: string) => {
    window.location.hash = `/${listPath}/${id}`;
    window.dispatchEvent(new Event("hashchange"));
  };

  return (
    <Paper sx={{ p: { xs: 1.25, sm: 2 }, mt: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
        Recent receipts
      </Typography>

      {isSmall ? (
        <Stack spacing={1} divider={<Divider flexItem />}>
          {rows.map((row) => (
            <RecentReceiptMobileCard
              key={`${row.type}-${row.id}`}
              row={row}
              onOpen={handleRowClick}
            />
          ))}
        </Stack>
      ) : (
        <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Receipt no.</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={`${row.type}-${row.id}`}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(row.listPath, row.id)}
                >
                  <TableCell>{row.date ? formatListDate(dayjs(row.date)) : "—"}</TableCell>
                  <TableCell>{TYPE_LABELS[row.type] ?? row.type}</TableCell>
                  <TableCell>{row.receiptNo || "—"}</TableCell>
                  <TableCell>{row.label || "—"}</TableCell>
                  <TableCell>{row.mode || "—"}</TableCell>
                  <TableCell align="right">{formatINR(row.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
